/**
 * Порядок карточек (G2 «Урок»): список 4–6 строк, у каждой кнопки «Выше»
 * и «Ниже» (fallback перетаскиванию, 44px), стрелки ↑/↓ на клавиатуре
 * делают то же. `order` — индексы исходных элементов сверху вниз.
 */
import { t } from '@life/i18n';

export interface OrderProps {
  label: string;
  /** Тексты элементов по исходным индексам. */
  items: string[];
  /** Пометки по исходным индексам (например, «сильный учитель»). */
  notes?: (string | undefined)[];
  order: number[];
  onChange: (order: number[]) => void;
}

/** Переставить позицию `pos` на `delta` строк; за края — без изменений. */
export function moveInOrder(order: readonly number[], pos: number, delta: number): number[] {
  const to = pos + delta;
  if (pos < 0 || pos >= order.length || to < 0 || to >= order.length) return [...order];
  const next = [...order];
  const [item] = next.splice(pos, 1);
  next.splice(to, 0, item as number);
  return next;
}

export function Order({ label, items, notes, order, onChange }: OrderProps) {
  const move = (pos: number, delta: number) => {
    const next = moveInOrder(order, pos, delta);
    if (next.some((v, i) => v !== order[i])) onChange(next);
  };

  return (
    <ol class="order" aria-label={label}>
      {order.map((item, pos) => (
        <li
          key={item}
          data-item={item}
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === 'ArrowUp') {
              e.preventDefault();
              move(pos, -1);
            } else if (e.key === 'ArrowDown') {
              e.preventDefault();
              move(pos, 1);
            }
          }}
        >
          <span class="mono order-n">{pos + 1}</span>
          <span class="order-text">
            {items[item]}
            {notes?.[item] && <span class="note">{notes[item]}</span>}
          </span>
          <span class="order-ctl">
            <button type="button" class="btn small" disabled={pos === 0} aria-label={t('ui.minigame.order.up')} onClick={() => move(pos, -1)}>
              {t('ui.minigame.order.up.short')}
            </button>
            <button
              type="button"
              class="btn small"
              disabled={pos === order.length - 1}
              aria-label={t('ui.minigame.order.down')}
              onClick={() => move(pos, 1)}
            >
              {t('ui.minigame.order.down.short')}
            </button>
          </span>
        </li>
      ))}
    </ol>
  );
}
